import * as React from 'react';

import { useQuery } from '@apollo/client/react';
import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';
import {
  Alert,
  Button,
  Divider as MuiDivider,
  Paper,
  Typography,
} from '@mui/material';
import {
  requestFacebookCode,
  requestGoogleCode,
} from '@realadvisor/auth-client';
import { MailOpen } from 'lucide-react';
import { Box, Flex } from 'react-system';

import { gql } from '@graphql/__generated__';
import * as config from '@legacy/config';
import { Markdown } from '@legacy/controls/markdown';
import { useLocale } from '@legacy/hooks/locale';
import { useTheme } from '@legacy/hooks/theme';
import { BtnFacebookLightNormalIos } from '@legacy/icons/btn-facebook-light-normal-ios';
import { BtnGoogleLightNormalIos } from '@legacy/icons/btn-google-light-normal-ios';
import { RealadvisorLogoNoText } from '@legacy/icons/realadvisor-logo-no-text';
import { LoginEmailForm } from '@legacy/routes/Login/LoginEmailForm';
import { LoginEmailVerifyCodeForm } from '@legacy/routes/Login/LoginEmailVerifyCodeForm';
import { LoginPhoneForm } from '@legacy/routes/Login/LoginPhoneForm';
import { LoginVerifyCodeForm } from '@legacy/routes/Login/LoginVerifyCodeForm';
import { ResendCodeButton } from '@legacy/routes/Login/ResendCodeButton';
import { ResendEmailCodeButton } from '@legacy/routes/Login/ResendEmailCodeButton';
import { getTenantSlugStageAndSubdomainFromHostname } from '@legacy/utils/tenant';

const Heading = ({ children }: { children: React.ReactNode }) => {
  const { text } = useTheme();
  return (
    <Box pb={3} css={[text.h6, { textAlign: 'center' }]}>
      {children}
    </Box>
  );
};

const generateButtonCss = (background: string): Interpolation<Theme> => ({
  '&&': {
    background,
    fontFamily: 'Roboto',
    fontSize: 14,
    fontWeight: 500,
    textAlign: 'left',
    justifyContent: 'flex-start',
    padding: 2,
  },
  '& .MuiButton-startIcon': {
    marginRight: 12,
    marginLeft: 0,
  },
  '& .MuiButton-startIcon > *:first-of-type': {
    fontSize: 40,
  },
});

const Divider = ({ children }: { children: React.ReactNode }) => (
  <MuiDivider
    sx={{
      my: '0.5em',
      color: 'text.primary',
      fontSize: '80%',
      fontWeight: 700,
      // theme-aware, subtle line instead of the old near-white #ddd
      '&::before, &::after': {
        borderColor: 'divider',
      },
    }}
  >
    {children}
  </MuiDivider>
);

const LoginError = () => {
  const { t } = useLocale();
  const { searchParams } = new URL(location.href);
  const error = searchParams.get('error');
  if (error == null) {
    return null;
  }
  let message = t('somethingWentWrong');
  if (error === 'EXPIRED_CODE') {
    message = t('authEmailExpired');
  } else if (error === 'EXPIRED_LINK') {
    message = t('authLinkExpired');
  }
  return (
    <Alert
      severity="error"
      css={{ marginBottom: 8, '.MuiAlert-icon': { display: 'none' } }}
    >
      <Markdown>{message}</Markdown>
    </Alert>
  );
};

const GET_TENANT_BY_SLUG = gql(/* GraphQL */ `
  query GetTenantBySlug($slug: String!) {
    tenants(where: { slug: { _eq: $slug } }) {
      id
    }
  }
`);

const LoginPage = ({
  onPhoneChange,
  onEmailCodeRequested,
  tenantId,
}: {
  onPhoneChange: (state: { number: string; tenantId: string | null }) => void;
  onEmailCodeRequested: (state: {
    email: string;
    tenantId: string | null;
  }) => void;
  tenantId: string | null;
}) => {
  const { t, language } = useLocale();
  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('return') ?? location.origin;

  return (
    <>
      <Heading>{t('signIn')}</Heading>
      <LoginError />

      <Button
        variant="contained"
        fullWidth
        startIcon={<BtnGoogleLightNormalIos />}
        css={generateButtonCss('#4285F4')}
        onClick={() => {
          const redirect_back_url = new URL('/login/google', location.origin);
          redirect_back_url.searchParams.set('redirect_uri', returnTo);
          if (tenantId != null) {
            redirect_back_url.searchParams.set('tenant_id', tenantId);
          }
          requestGoogleCode({
            client_id: config.googleClientId,
            redirect_back_uri: redirect_back_url.toString(),
          });
        }}
      >
        {t('Sign in with Google')}
      </Button>
      <Box py={1}>
        <Button
          variant="contained"
          startIcon={<BtnFacebookLightNormalIos />}
          css={generateButtonCss('#4c6ca6')}
          fullWidth
          onClick={() => {
            const redirect_back_url = new URL(
              '/login/facebook',
              location.origin,
            );
            redirect_back_url.searchParams.set('redirect_uri', returnTo);
            if (tenantId != null) {
              redirect_back_url.searchParams.set('tenant_id', tenantId);
            }
            requestFacebookCode({
              client_id: config.facebookAppID,
              redirect_back_uri: redirect_back_url.toString(),
            });
          }}
        >
          {t('Sign in with Facebook')}
        </Button>
      </Box>
      <Divider>{t('or')}</Divider>
      <LoginEmailForm
        tenantId={tenantId}
        onCodeRequested={onEmailCodeRequested}
      />
      <Divider>{t('or')}</Divider>
      <LoginPhoneForm onChange={onPhoneChange} tenantId={tenantId} />
      <div
        css={{ marginTop: 16, fontSize: 12, textAlign: 'justify' }}
        dangerouslySetInnerHTML={{
          __html: t('loginDisclaimer', {
            privacyHref: `https://realadvisor.${
              language === 'es' ? 'es' : 'ch'
            }/${language}/privacy`,
            termsHref: `https://realadvisor.${
              language === 'es' ? 'es' : 'ch'
            }/${language}/terms`,
          }),
        }}
      />
      <div
        css={{ marginTop: 16, fontSize: 12, textAlign: 'justify' }}
        dangerouslySetInnerHTML={{
          __html: t('loginGoogleDisclaimer', {
            dataPolicyLink: `https://developers.google.com/terms/api-services-user-data-policy`,
          }),
        }}
      />
    </>
  );
};

const VerifyPage = ({
  phone,
  tenantId,
  onBack,
}: {
  phone: string;
  tenantId: string | null;
  onBack: () => void;
}) => {
  const { t } = useLocale();
  return (
    <>
      <Heading>{t('enterSMSCode')}</Heading>
      <LoginVerifyCodeForm phone={phone} tenantId={tenantId} />
      <Divider>{t('or')}</Divider>
      <ResendCodeButton phone={phone} tenantId={tenantId} onCancel={onBack} />
      <Flex py={1} justifyContent="center">
        <Button variant="outlined" onClick={onBack}>
          {t('Back')}
        </Button>
      </Flex>
    </>
  );
};

const EmailVerifyPage = ({
  email,
  tenantId,
  onBack,
}: {
  email: string;
  tenantId: string | null;
  onBack: () => void;
}) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  return (
    <>
      <Flex justifyContent="center" pb={2}>
        <Flex
          alignItems="center"
          justifyContent="center"
          css={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            background: colors.blue50,
            color: colors.primaryMain,
          }}
        >
          <MailOpen size={26} strokeWidth={1.8} />
        </Flex>
      </Flex>

      <Heading>{t('Enter the code we emailed you')}</Heading>

      <Box
        css={{ textAlign: 'center', fontSize: 14, color: colors.mediumText }}
      >
        {t('We sent a code to')}
      </Box>
      <Flex justifyContent="center" pt={1} pb={3}>
        <span
          css={{
            maxWidth: '100%',
            padding: '4px 12px',
            borderRadius: 999,
            background: colors.grey100,
            border: `1px solid ${colors.grey200}`,
            fontSize: 13,
            fontWeight: 600,
            color: colors.highText,
            // Long addresses must not push the card wider than the viewport.
            overflowWrap: 'anywhere',
          }}
        >
          {email}
        </span>
      </Flex>

      <LoginEmailVerifyCodeForm email={email} tenantId={tenantId} />

      <Divider>{t('or')}</Divider>
      <ResendEmailCodeButton email={email} tenantId={tenantId} />
      <Flex py={1} justifyContent="center">
        <Button variant="outlined" onClick={onBack}>
          {t('Back')}
        </Button>
      </Flex>
    </>
  );
};

export const Login = () => {
  const { t } = useLocale();
  const [phoneState, setPhoneState] = React.useState<{
    number: string;
    tenantId: string | null;
  } | null>(null);
  const [emailState, setEmailState] = React.useState<{
    email: string;
    tenantId: string | null;
  } | null>(null);

  let { slug, stage, subdomain } = getTenantSlugStageAndSubdomainFromHostname(
    location.hostname,
  );

  // we are accessing dev-crm without tenantSlug only stage, but regex match only slug
  // or we are accessing local or prod without slug
  if (
    (subdomain === 'dev-crm' && stage == null && slug != null) ||
    slug == null
  ) {
    slug = '';
  }

  const { data, loading } = useQuery(GET_TENANT_BY_SLUG, {
    variables: { slug },
    skip: slug === '',
  });

  const tenantId: string | null = data?.tenants[0]?.id ?? null;

  if (loading) {
    return null;
  }

  return (
    <Flex
      width={1}
      py={3}
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      // vh is broken in iOS
      css={{ minHeight: window.innerHeight }}
    >
      <Typography
        variant="h5"
        component="div"
        css={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 16,
        }}
      >
        <RealadvisorLogoNoText size={32} css={{ marginRight: 8 }} />
        RealAdvisor
      </Typography>
      <Typography variant="h4" css={{ marginBottom: 32 }}>
        <strong>RealAdvisor CRM</strong>
      </Typography>
      {slug !== '' && tenantId == null ? (
        <Alert severity="error">
          <Markdown>{t('Tenant not found')}</Markdown>
        </Alert>
      ) : (
        <Paper elevation={4}>
          <Box
            // prevent captcha overflow
            width={['300px', '360px']}
            py={[3, 4]}
            px={[2, 4]}
            css={{ overflow: 'hidden' }}
          >
            {phoneState != null ? (
              <VerifyPage
                phone={phoneState.number}
                tenantId={phoneState.tenantId}
                onBack={() => setPhoneState(null)}
              />
            ) : emailState != null ? (
              <EmailVerifyPage
                email={emailState.email}
                tenantId={emailState.tenantId}
                onBack={() => setEmailState(null)}
              />
            ) : (
              <LoginPage
                onPhoneChange={setPhoneState}
                onEmailCodeRequested={setEmailState}
                tenantId={tenantId}
              />
            )}
          </Box>
        </Paper>
      )}
    </Flex>
  );
};
